import { createFileRoute } from "@tanstack/react-router";
import { SeoPage } from "@/components/SeoPage";
import { faqJsonLd, articleJsonLd } from "@/lib/seo-jsonld";
import { breadcrumbJsonLd } from "@/lib/seo-jsonld";

const URL = "https://alkoholprovet.se/kunskapsbank/kunskapsprovet-umea";

const faqs = [
  { q: "Var gör jag kunskapsprovet i Umeå?", a: "Umeå kommuns tillståndsenhet vid Socialnämnden administrerar provet, som skrivs på plats i Stadshuset eller annan utsedd lokal. Bokning sker via e-tjänsten på umea.se." },
  { q: "Hur lång är väntetiden i Umeå?", a: "Normalt 3–6 veckor från ansökan till provtillfälle. Något längre under student- och festivalsäsongen." },
  { q: "Vad kostar kunskapsprovet i Umeå?", a: "Ansökningsavgift för stadigvarande tillstånd är ca 10 000–12 000 kr. Omprov 1 500–2 500 kr." },
  { q: "Kan jag göra provet på engelska?", a: "Ja, Umeå kommun erbjuder provet på engelska, vilket är värdefullt med stor andel internationella studenter och företagare i staden." },
  { q: "Hur lång är handläggningstiden för tillstånd i Umeå?", a: "Räkna med 2–4 månader från komplett ansökan till beslut för stadigvarande tillstånd." },
];

const sections = [
  { h2: "Tillståndsenheten i Umeå", body: "Umeå kommuns tillståndsenhet är placerad under Socialnämnden och ansvarar för serveringstillstånd inom kommunen, från restauranger och hotell till nattklubbar och studentpubar. Som universitetsstad har Umeå en livaktig restaurangmarknad med stor andel studentnära verksamheter, vilket gör att enheten har erfarenhet av många typer av ärenden. Ansökan sker via e-tjänsten på umea.se med BankID." },
  { h2: "Ansökan – steg för steg", body: "1. Förbered alla dokument: bolagsbevis, hyreskontrakt, ritning av lokalen, finansiering, PBI och verksamhetsbeskrivning.\n2. Lämna in ansökan via e-tjänsten på umea.se.\n3. Betala ansökningsavgift.\n4. Boka kunskapsprov – väntetid 3–6 veckor.\n5. Skriv provet i Stadshuset (90 min, papper).\n6. Vänta in remissvar från polis (Norrbotten/Västerbotten), skatteverket m.fl.\n7. Få beslut – normalt 2–4 månader." },
  { h2: "Väntetider i Umeå", body: "Umeås tillståndsenhet har generellt korta väntetider:\n• Vinter (jan–mars): kortast.\n• Vår (april–maj): ökat tryck inför sommaren och kulturhuvudstadens festivaler.\n• Sommar: hög belastning – planera extra tid.\n• Höst: terminstart innebär många nya studentpubar och kafévärldar.\n\nFestivaler som Brännbollsyran och Umeå Open kan tillfälligt höja belastningen." },
  { h2: "Kostnader i Umeå", body: "• Stadigvarande tillstånd: 10 000–12 000 kr.\n• Tillfälligt tillstånd: 2 000–6 000 kr.\n• Omprov: 1 500–2 500 kr.\n• Årlig tillsynsavgift: 3 000–10 000 kr beroende på omsättning.\n\nAvgifter fastställs av kommunfullmäktige." },
  { h2: "Provdagen i Umeå", body: "Provet skrivs på papper under tystnad. Du har 90 minuter. Ta med giltig legitimation. Mobil och anteckningar är inte tillåtna. Resultat meddelas skriftligt inom 1–2 veckor. Vid underkänt kan omprov bokas tidigast efter 14 dagar." },
  { h2: "Tips för Umeå-sökande", body: "Umeås tillståndsenhet är effektiv men hård på lämplighetsprövning för verksamheter nära universitetsområdet eller skolor. Lägg särskild vikt vid:\n• Verksamhetsbeskrivning – visa hur ni hanterar berusningsgrad och åldersgränskontroll.\n• Personalplan – tillräcklig serveringspersonal och dörrvakter där det krävs.\n• Egenkontrollprogram – tydliga rutiner.\n\nFörbered kunskapsprovet med vår plattform – 800 frågor uppdaterade för 2026 års regler." },
];

function Page() {
  return (
    <SeoPage
      breadcrumbs={[
        { label: "Hem", to: "/" },
        { label: "Kunskapsbank", to: "/kunskapsbank" },
        { label: "Kunskapsprovet Umeå" },
      ]}
      title="Kunskapsprovet serveringstillstånd Umeå – guide 2026"
      intro="Allt om kunskapsprovet för serveringstillstånd i Umeå: ansökan, väntetider, kostnader och praktiska tips. Uppdaterat för 2026 års regler."
      sections={sections}
      faqs={faqs}
      lastReviewed="2026-06-15"
      related={{
        title: "Läs vidare",
        links: [
          { to: "/kunskapsbank/kunskapsprovet-orebro", label: "Kunskapsprovet Örebro" },
          { to: "/kunskapsbank/kunskapsprovet-jonkoping", label: "Kunskapsprovet Jönköping" },
          { to: "/kunskapsbank/kunskapsprovet-vaxjo", label: "Kunskapsprovet Växjö" },
          { to: "/kunskapsbank/kunskapsprovet-stockholm", label: "Kunskapsprovet Stockholm" },
          { to: "/practice", label: "Träna på kunskapsprovet" },
        ],
      }}
      sources={[
        { label: "Umeå kommun – serveringstillstånd", href: "https://www.umea.se/" },
        { label: "Alkohollagen (2010:1622)", href: "https://www.riksdagen.se/sv/dokument-och-lagar/dokument/svensk-forfattningssamling/alkohollag-20101622_sfs-2010-1622/" },
      ]}
    />
  );
}

export const Route = createFileRoute("/kunskapsbank/kunskapsprovet-umea")({
  component: Page,
  head: () => ({
    meta: [
      { title: "Kunskapsprovet Umeå – ansökan, väntetid & kostnad 2026" },
      { name: "description", content: "Allt om kunskapsprovet för serveringstillstånd i Umeå: ansökan, väntetider, kostnader och tips. Uppdaterat 2026." },
      { property: "og:title", content: "Kunskapsprovet Umeå – komplett guide 2026" },
      { property: "og:description", content: "Allt om kunskapsprovet för serveringstillstånd i Umeå." },
      { property: "og:type", content: "article" },
      { property: "og:url", content: URL },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      faqJsonLd(faqs),
      breadcrumbJsonLd([
        { label: "Hem", url: "https://alkoholprovet.se/" },
        { label: "Kunskapsbank", url: "https://alkoholprovet.se/kunskapsbank" },
        { label: "Kunskapsprovet Umeå", url: URL },
      ]),
      articleJsonLd({
        url: URL,
        headline: "Kunskapsprovet Umeå 2026",
        description: "Komplett guide till kunskapsprovet för serveringstillstånd i Umeå.",
        datePublished: "2026-06-15",
        dateModified: "2026-06-15",
      }),
    ],
  }),
});
